import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal, recomputeUpsellAmount } from "@/hooks/use-budget-lines";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

/**
 * VIB-695-followup: Importér mersalg fra opplastet PDF/screenshot.
 *
 * Gjenbruker samme flow som "Nytt prosjekt"-wizardens Tilbud-/Budsjett-steg:
 * Vision-extraction → template-match → editable budgetLines → insert som
 * upsell-container + budget_lines i ett kall. PDF-en lagres i `offers`-
 * bucket og path/filnavn lagres på upsell-raden så vedlegget kan vises i
 * MersalgTab og UpsellEditDialog etterpå.
 *
 * Filopplasting skjer FØR insert, slik at hvis storage feiler kommer ingen
 * halv-rad inn i databasen.
 */
export interface ImportUpsellLine {
  phase: string;
  description: string;
  quantity_hours: number;
  amount: number;
  estimated_cost: number;
  cost_type: "internal" | "external";
  template_id: string | null;
}

export function useImportUpsellFromPdf() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      description: string;
      lines: ImportUpsellLine[];
      overhead_pct: number | null;
      discount_pct: number;
      discount_amount: number;
      /** Opplastet PDF/screenshot — lagres i `offers` storage-bucket. Valgfri. */
      file: File | null;
    }): Promise<string> => {
      if (input.lines.length === 0) {
        throw new Error("Ingen linjer å importere");
      }

      // 1) Last opp PDF først (hvis gitt). Hvis dette feiler, ingen rader
      //    kommer inn i databasen.
      let attachment_path: string | null = null;
      let attachment_filename: string | null = null;
      if (input.file) {
        const safeName = input.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `upsells/${input.project_id}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("offers")
          .upload(path, input.file, { upsert: false });
        if (upErr) throw new Error(`Filopplasting feilet: ${upErr.message}`);
        attachment_path = path;
        attachment_filename = input.file.name;
      }

      // 2) Opprett upsell-container som draft med vedlegg-metadata.
      const { data: upsell, error: upsErr } = await supabase
        .from("upsells")
        .insert({
          project_id: input.project_id,
          description: input.description.trim() || "Mersalg fra import",
          amount_excl_vat: 0,
          kind: "cost",
          status: "draft",
          overhead_pct: input.overhead_pct,
          discount_pct: input.discount_pct,
          discount_amount: input.discount_amount,
          attachment_path,
          attachment_filename,
        })
        .select("id")
        .single();
      if (upsErr) throw new Error(upsErr.message);

      // 3) Insert budget_lines knyttet til upsell.
      // VIB-2866: batch-insert gir identisk created_at; bær importrekkefølgen.
      const rows = input.lines.map((l, index) => ({
        project_id: input.project_id,
        upsell_id: upsell.id,
        sort_order: (index + 1) * 10,
        phase: l.phase,
        description: l.description,
        cost_type: l.cost_type,
        unit: l.cost_type === "internal" ? "t" : "stk",
        quantity: l.cost_type === "internal" ? 0 : 1,
        quantity_hours: l.quantity_hours,
        amount: l.amount,
        estimated_cost: l.estimated_cost,
        template_id: l.template_id,
      }));
      const { error: blErr } = await supabase.from("budget_lines").insert(rows);
      if (blErr) throw new Error(blErr.message);

      await recomputeUpsellAmount(upsell.id as string);
      await recomputeProjectTotal(input.project_id);
      return upsell.id as string;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Import feilet: ${(err as Error).message}`);
    },
  });
}

/**
 * VIB-695-followup: Hent en signed URL for et upsell-vedlegg i `offers`-
 * bucket. Returnerer null hvis path mangler eller signing feiler.
 */
export async function getUpsellAttachmentUrl(path: string): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from("offers")
    .createSignedUrl(path, 60 * 60); // 1 time
  if (error) return null;
  return data?.signedUrl ?? null;
}
